// A return visit on an existing job (gap #5: appointments.add): after a tech pauses with "Needs
// parts" or "Return visit", the office books the next visit on the same job instead of a new one.
// Days count from the company's today; a window already started today isn't offered. The visit
// starts scheduled (the board dispatches it); the server says why when it refuses (a completed job).
// For those who dispatch (jobs.dispatch); Review and the board's visit panel use it.

import { useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { sentences } from '@flowology/kernel';
import { client } from '../api.ts';
import {
  DAY_WINDOWS,
  dayOptions,
  hourNow,
  today as todayIn,
  windowLabel,
  windowStarted,
  zonedInstant,
} from '../book-model.ts';
import { Button as FlButton, SelectField } from '@flowology/ui-web/fl';
import { useDesign } from '../blueprint/design.ts';
import { SaveButton, useSaveKey } from './SaveButton.tsx';
import './add-visit.css';

interface AddVisitProps {
  jobId: string;
  jobNumber: string;
  /** The tech who had the job, offered first. */
  leadTechId: string | null;
  timeZone: string;
  /** Said once the visit is booked ("Return visit on Tue, Oct 20, 8–10 a.m. …"). */
  onAdded: (said: string) => void;
}

/** Riverstone draws the app's own form; Blueprint the same fields on ui-web's SelectField and Button. */
export function AddVisit(props: AddVisitProps) {
  const a = useAddVisit(props);
  return useDesign() === 'blueprint' ? (
    <BpAddVisit a={a} jobNumber={props.jobNumber} />
  ) : (
    <ClassicAddVisit a={a} jobNumber={props.jobNumber} />
  );
}

type AddVisitState = ReturnType<typeof useAddVisit>;

/** The return visit's state and its write, for both drawings: the day, the window, the tech, and Add. */
function useAddVisit({ jobId, jobNumber, leadTechId, timeZone, onAdded }: AddVisitProps) {
  const [open, setOpen] = useState(false);
  const [techs, setTechs] = useState<{ id: string; name: string }[]>([]);
  const today = todayIn(timeZone);
  const nowH = hourNow(timeZone);
  const days = dayOptions(today, 14);
  const firstWindow = (day: string) => {
    const i = DAY_WINDOWS.findIndex((w) => !windowStarted(day, today, w, nowH));
    return i < 0 ? 0 : i;
  };
  const opens = DAY_WINDOWS.every((w) => windowStarted(today, today, w, nowH)) ? days[1]!.date : today;
  const [day, setDay] = useState(opens);
  const [win, setWin] = useState(() => firstWindow(opens));
  const [tech, setTech] = useState(leadTechId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = useSaveKey(JSON.stringify([jobId, day, win, tech]));
  useEffect(() => {
    if (!open || techs.length) return;
    void client
      .call<{ staff: { id: string; displayName: string }[] }>('staff.list', { techniciansOnly: true })
      .then((r) => setTechs(r.staff.map((s) => ({ id: s.id, name: s.displayName }))))
      .catch(() => setTechs([]));
  }, [open, techs.length]);

  const started = windowStarted(day, today, DAY_WINDOWS[win]!, nowH);
  const add = async () => {
    const [s0, e0] = DAY_WINDOWS[win]!;
    const start = zonedInstant(day, s0, timeZone);
    const end = zonedInstant(day, e0, timeZone);
    setBusy(true);
    setError(null);
    try {
      await client.call(
        'appointments.add',
        {
          jobId,
          start,
          end,
          arrivalWindow: { start, end },
          techIds: tech ? [tech] : [],
          ...(tech ? { leadTechId: tech } : {}),
        },
        { idempotencyKey: save.key },
      );
      save.renew();
      setOpen(false);
      const label = days.find((d) => d.date === day)?.label ?? day;
      onAdded(
        sentences(
          `Return visit on #${jobNumber}: ${label === 'Today' || label === 'Tomorrow' ? label.toLowerCase() : label}, ${windowLabel(DAY_WINDOWS[win]!)}`,
          'Dispatch it from the board on the day',
        ),
      );
    } catch (e) {
      setError(
        e instanceof RpcError ? e.message : 'Couldn’t add the visit. Try again; it won’t be added twice.',
      );
    } finally {
      setBusy(false);
    }
  };
  return {
    open,
    setOpen,
    techs,
    today,
    nowH,
    days,
    firstWindow,
    day,
    setDay,
    win,
    setWin,
    tech,
    setTech,
    busy,
    error,
    started,
    add,
  };
}

function ClassicAddVisit({ a, jobNumber }: { a: AddVisitState; jobNumber: string }) {
  const {
    open,
    setOpen,
    techs,
    today,
    nowH,
    days,
    firstWindow,
    day,
    setDay,
    win,
    setWin,
    tech,
    setTech,
    busy,
    error,
    started,
    add,
  } = a;
  if (!open)
    return (
      <button className="btn soft" onClick={() => setOpen(true)}>
        Add a return visit
      </button>
    );
  return (
    <div className="move add-visit" role="group" aria-label={`Return visit on #${jobNumber}`}>
      <label>
        Day
        <select
          className="field"
          value={day}
          onChange={(e) => {
            setDay(e.target.value);
            setWin(firstWindow(e.target.value));
          }}
        >
          {days.map((d) => (
            <option key={d.date} value={d.date}>
              {d.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        Arrival window
        <select className="field" value={win} onChange={(e) => setWin(Number(e.target.value))}>
          {DAY_WINDOWS.map((w, i) => (
            <option key={i} value={i} disabled={windowStarted(day, today, w, nowH)}>
              {windowLabel(w)}
              {windowStarted(day, today, w, nowH) ? ' (already started)' : ''}
            </option>
          ))}
        </select>
      </label>
      <label>
        Technician
        <select className="field" value={tech} onChange={(e) => setTech(e.target.value)}>
          <option value="">Choose on the board</option>
          {techs.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      <div className="row">
        <button className="btn soft" onClick={() => setOpen(false)}>
          Cancel
        </button>
        <SaveButton
          busy={busy}
          busyLabel="Adding…"
          error={error}
          disabled={started}
          onClick={() => void add()}
        >
          Add the visit
        </SaveButton>
      </div>
    </div>
  );
}

/** Blueprint's: Add a return visit opens the same three choices in the panel's flow, then Cancel and Add. */
function BpAddVisit({ a, jobNumber }: { a: AddVisitState; jobNumber: string }) {
  const {
    open,
    setOpen,
    techs,
    today,
    nowH,
    days,
    firstWindow,
    day,
    setDay,
    win,
    setWin,
    tech,
    setTech,
    busy,
    error,
    started,
    add,
  } = a;
  if (!open)
    return (
      <FlButton size="touch-on-phone" onPress={() => setOpen(true)}>
        Add a return visit
      </FlButton>
    );
  return (
    <div className="bp-addvisit" role="group" aria-label={`Return visit on #${jobNumber}`}>
      <SelectField
        label="Day"
        value={day}
        onChange={(d) => {
          setDay(d);
          setWin(firstWindow(d));
        }}
        options={days.map((d) => ({ value: d.date, label: d.label }))}
      />
      <SelectField
        label="Arrival window"
        value={String(win)}
        onChange={(w) => setWin(Number(w))}
        options={DAY_WINDOWS.map((w, i) => {
          const gone = windowStarted(day, today, w, nowH);
          return {
            value: String(i),
            label: `${windowLabel(w)}${gone ? ' (already started)' : ''}`,
            isDisabled: gone,
          };
        })}
      />
      <SelectField
        label="Technician"
        value={tech}
        onChange={setTech}
        options={[
          { value: '', label: 'Choose on the board' },
          ...techs.map((t) => ({ value: t.id, label: t.name })),
        ]}
      />
      <div className="bp-addvisit__acts">
        <FlButton size="touch-on-phone" onPress={() => setOpen(false)}>
          Cancel
        </FlButton>
        <FlButton
          variant="primary"
          size="touch-on-phone"
          isBusy={busy}
          isDisabled={started}
          onPress={() => void add()}
        >
          Add the visit
        </FlButton>
      </div>
      {error && !busy && (
        <p className="bp-addvisit__why" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
